import { batch } from 'react-redux'
import { addDays } from 'date-fns'
import { getDistricts, getConfigData } from '../../services/config'
import { getQuoteStepOne, saveQuoteStepOne } from '../../services/quote'
import { makeActionCreator } from '../../config/store/utils'
import { formatDate } from '@chilecompra/react-kit'
import {
  onGetQuoteNotificationsSuccess,
  onSetEnvironmentalValue,
  onSetSocialEconomicValue
} from '../QuoteStepTwo/QuoteStepTwo.actions'
import { formatTypeCurrency } from '../../modules/utils/formatTypeCurrency'

export const GET_QUOTE_BY_CODE = 'GET_QUOTE_BY_CODE'
export const GET_QUOTE_BY_CODE_ERROR = 'GET_QUOTE_BY_CODE_ERROR'
export const GET_QUOTE_BY_CODE_SUCCESS = 'GET_QUOTE_BY_CODE_SUCCESS'
export const onGetQuoteByCode = makeActionCreator(GET_QUOTE_BY_CODE)
export const onGetQuoteByCodeError = makeActionCreator(GET_QUOTE_BY_CODE_ERROR, 'payload')
export const onGetQuoteByCodeSuccess = makeActionCreator(GET_QUOTE_BY_CODE_SUCCESS, 'payload')
export const onGetQuoteByCodeThunk = payload => async dispatch => {
  dispatch(onGetQuoteByCode())
  try {
    const { data } = await getQuoteStepOne({ code: payload.code })
    const quote = {
      closeDate: new Date(data?.payload?.fechaCierre),
      code: data?.payload?.codigoSolicitud,
      contact: {
        name: data?.payload?.nombreContacto,
        phone: data?.payload?.telefonoContacto,
        email: data?.payload?.emailContacto
      },
      delivery: {
        address: data?.payload?.direccion,
        district: {
          id: data?.payload?.comuna?.id,
          name: data?.payload?.comuna.nombre
        },
        region: {
          id: data?.payload?.region?.id,
          name: data?.payload?.region?.nombre
        },
        period: data?.payload?.diasHabiles
      },
      description: data?.payload?.descripcion,
      estimatedBudget: {
        currency: data?.payload?.moneda,
        totalAmount: formatTypeCurrency(data?.payload?.moneda, data?.payload?.montoTotal.toString())
      },
      id: data?.payload?.id,
      name: data?.payload?.nombre,
      products: data?.payload?.productos,
      state: data?.payload?.estado,
      finesPenalties: data?.payload?.esMultaSancion,
      secondCallAutomatic: data?.payload?.marca2doLlamado
    }
    const providers =
      data?.payload?.proveedores?.map(item => ({
        dni: item.rut,
        id: item.id,
        name: item.razonSocial
      })) || []

    const isEnvironmental = data?.payload?.consideraRequisitosMedioambientales
    const isSocialEconomic = data?.payload?.consideraRequisitosImpactoSocialEconomico

    const booleanToYesNo = value => {
      if (value === undefined || value === null) return ''

      return value ? 'yes' : 'no'
    }

    return batch(() => {
      dispatch(onGetQuoteByCodeSuccess({ quote }))
      dispatch(onSetEnvironmentalValue({ isEnvironmental: booleanToYesNo(isEnvironmental) }))
      dispatch(onSetSocialEconomicValue({ isSocialEconomic: booleanToYesNo(isSocialEconomic) }))
      dispatch(
        onGetQuoteNotificationsSuccess({
          companySizeFilter: 0,
          sellersAdded: providers
        })
      )
      dispatch(onGetDistrictsThunk({ stateID: quote?.delivery?.region?.id }))
    })
  } catch (error) {
    return dispatch(onGetQuoteByCodeError(error))
  }
}

export const GET_DISTRICTS = 'GET_DISTRICTS'
export const GET_DISTRICTS_ERROR = 'GET_DISTRICTS_ERROR'
export const GET_DISTRICTS_SUCCESS = 'GET_DISTRICTS_SUCCESS'
export const onGetDistricts = makeActionCreator(GET_DISTRICTS)
export const onGetDistrictsError = makeActionCreator(GET_DISTRICTS_ERROR, 'payload')
export const onGetDistrictsSuccess = makeActionCreator(GET_DISTRICTS_SUCCESS, 'payload')
export const onGetDistrictsThunk = payload => async dispatch => {
  dispatch(onGetDistricts())
  try {
    const { data } = await getDistricts({ stateID: payload.stateID })
    const districts = data.payload?.map(district => ({ name: district.nombre, value: district.id })) || []

    dispatch(onGetDistrictsSuccess({ districts }))
  } catch (error) {
    return dispatch(onGetDistrictsError(error))
  }
}

export const GET_INITIAL_VALUE_QUOTE = 'GET_INITIAL_VALUE_QUOTE'
export const GET_INITIAL_VALUE_QUOTE_ERROR = 'GET_INITIAL_VALUE_QUOTE_ERROR'
export const GET_INITIAL_VALUE_QUOTE_SUCCESS = 'GET_INITIAL_VALUE_QUOTE_SUCCESS'
export const onGetInitialValues = makeActionCreator(GET_INITIAL_VALUE_QUOTE)
export const onGetInitialValuesError = makeActionCreator(GET_INITIAL_VALUE_QUOTE_ERROR, 'payload')
export const onGetInitialValuesSuccess = makeActionCreator(GET_INITIAL_VALUE_QUOTE_SUCCESS, 'payload')
export const onGetInitialValuesThunk = () => async dispatch => {
  dispatch(onGetInitialValues())
  try {
    const { data } = await getConfigData()
    const newServerDate = new Date(data.payload.fechaServidor)
    const maxDate = data.payload?.fechaMaxima
    const exchangeRates =
      data.payload?.monedas?.map(exchangeRate => ({
        clpAmount: exchangeRate.valorPeso,
        decimals: exchangeRate.decimales,
        name: exchangeRate.nombre,
        value: exchangeRate.moneda
      })) || []
    const orderedExchangeRates = exchangeRates.sort((a, b) => a.name.localeCompare(b.name))

    return dispatch(
      onGetInitialValuesSuccess({
        minDate: formatDate(addDays(newServerDate, 2), 'yyyy-MM-dd'),
        maxDate,
        exchangeRates: orderedExchangeRates,
        maxAmountsUTMs: data.payload.montoMaximoUTMs,
        maxDays: data.payload.limiteEntrega,
        maxClosingDate: data.payload.fechaCierreMaxima,
        minAmountPesos: data.payload.montoMinimoPesos,
        serverDate: data.payload.fechaServidor
      })
    )
  } catch (error) {
    return dispatch(onGetInitialValuesError(error))
  }
}

export const SAVE_QUOTE_STEP_ONE = 'SAVE_QUOTE_STEP_ONE'
export const SAVE_QUOTE_STEP_ONE_ERROR = 'SAVE_QUOTE_STEP_ONE_ERROR'
export const SAVE_QUOTE_STEP_ONE_SUCCESS = 'SAVE_QUOTE_STEP_ONE_SUCCESS'
export const onSaveQuoteStepOne = makeActionCreator(SAVE_QUOTE_STEP_ONE)
export const onSaveQuoteStepOneError = makeActionCreator(SAVE_QUOTE_STEP_ONE_ERROR, 'payload')
export const onSaveQuoteStepOneSuccess = makeActionCreator(SAVE_QUOTE_STEP_ONE_SUCCESS, 'payload')
export const onSaveQuoteStepOneThunk = payload => async dispatch => {
  dispatch(onSaveQuoteStepOne())

  try {
    const quote = {
      address: payload.address,
      amount: payload.amount,
      closingDate: formatDate(payload.closingDate, 'yyyy-MM-dd') + ' ' + formatDate(payload.closingTime, 'HH:mm:ss'),
      contactPhone: '+56 ' + payload.contactPhone,
      contactName: payload.contactName,
      contactEmail: payload.contactEmail,
      detail: payload.detail,
      districtCode: payload.districtCode,
      deliveryTime: Number(payload.deliveryTime),
      exchangeRate: payload.exchangeRate,
      regionCode: payload.regionCode,
      name: payload.name,
      secondCallAutomatic: payload.secondCallAutomatic,
      budgetType: payload.budgetType
    }

    if (payload.code !== undefined) quote.code = payload.code

    const { data } = await saveQuoteStepOne(quote)

    return batch(() => {
      dispatch(onSaveQuoteStepOneSuccess())
      dispatch(onGetQuoteByCodeThunk({ code: data.payload.codigoSolicitud }))
      payload.callback({ quoteCode: data.payload.codigoSolicitud })
    })
  } catch (error) {
    const err = error.originalError.response.data.errores.map(error => ({
      code: error.codigo,
      description: error.descripcion
    }))

    return batch(() => {
      dispatch(onOpenQuoteSaveErrorDialog())
      dispatch(onSaveQuoteStepOneError({ error: err }))
    })
  }
}

export const RESET_QUOTE_STEP_ONE_STATE = 'RESET_QUOTE_STEP_ONE_STATE'
export const onResetQuoteStepOneState = makeActionCreator(RESET_QUOTE_STEP_ONE_STATE)

export const SHOW_QUOTE_SAVED_SATISFACTORY_DIALOG = 'SHOW_QUOTE_SAVED_SATISFACTORY_DIALOG'
export const onOpenQuoteSavedSatisfactoryDialog = makeActionCreator(SHOW_QUOTE_SAVED_SATISFACTORY_DIALOG, {})

export const CLOSE_QUOTE_SAVED_SATISFACTORY_DIALOG = 'CLOSE_QUOTE_SAVED_SATISFACTORY_DIALOG'
export const onCloseQuoteSavedSatisfactoryDialog = makeActionCreator(CLOSE_QUOTE_SAVED_SATISFACTORY_DIALOG, {})

export const SHOW_QUOTE_SAVE_ERROR_DIALOG = 'SHOW_QUOTE_SAVE_ERROR_DIALOG'
export const onOpenQuoteSaveErrorDialog = makeActionCreator(SHOW_QUOTE_SAVE_ERROR_DIALOG, {})

export const CLOSE_QUOTE_SAVE_ERROR_DIALOG = 'CLOSE_QUOTE_SAVE_ERROR_DIALOG'
export const onCloseQuoteSaveErrorDialog = makeActionCreator(CLOSE_QUOTE_SAVE_ERROR_DIALOG, {})

export const CLEAR_FORM_STEP_ONE = 'CLEAR_FORM_STEP_ONE'
export const onClearFormStepOne = makeActionCreator(CLEAR_FORM_STEP_ONE, {})

export const BUDGET_TYPE_ERROR = 'BUDGET_TYPE_ERROR'
export const onBudgetTypeError = makeActionCreator(BUDGET_TYPE_ERROR, 'payload')

export const ON_SECOND_CALL_AUTOMATIC = 'ON_SECOND_CALL_AUTOMATIC'
export const onSeconCallAutomatic = makeActionCreator(ON_SECOND_CALL_AUTOMATIC, 'payload')

export const OFF_SECOND_CALL_AUTOMATIC = 'OFF_SECOND_CALL_AUTOMATIC'
export const onOffSeconCallAutomatic = makeActionCreator(OFF_SECOND_CALL_AUTOMATIC, 'payload')
